import type { FC, ReactElement } from 'react';
import React, { Children, cloneElement, useMemo, isValidElement, useCallback } from 'react';
import cn from 'classnames';

import type { TAccordionItem, TAccordionHeader, TAccordionPanel } from '../models';
import { useAccordionContext } from '../context';
import type mobileStyles from '../styles/mobile.css';

const ACCORDION_ITEM = 'accordion-item';

export const Item: FC<TAccordionItem> = ({
    Stack,
    backgroundColor,
    borderRadius = 32,
    children,
    className,
    hovered = false,
    id,
    isLastChild,
    isMobile,
    panelKey = 0,
    prevChildBackgroundColor,
    styles,
}): JSX.Element => {
    const { dividerEnabled, newDesign, openedValue, theme } = useAccordionContext();

    const getAccordionItemId = useCallback(
        (elementId = id, panelKeyNumber = panelKey) =>
            `${ACCORDION_ITEM}-${elementId}-${panelKeyNumber}`,
        [id, panelKey]
    );

    /*
     TODO разобраться с типизацией пропа child (Children.map) в стрелочной ф-ии,
     т.к. JSX.Element было временным решением чтобы продолжить работу команд
     создана таска https://jira.moscow.alfaintra.net/browse/ALFABANKRU-62250
     */

    const isOpenItem = useMemo(() => openedValue?.includes(id), [openedValue, id]);
    const isPositionBeforeOpenedItem = useMemo(() => {
        if (typeof document === 'undefined' || openedValue?.includes(id)) return false;

        const currentElement = document.getElementById(getAccordionItemId());

        return openedValue?.some(
            value =>
                document.getElementById(getAccordionItemId(value, panelKey + 1)) ===
                currentElement?.nextElementSibling
        );
    }, [getAccordionItemId, id, openedValue, panelKey]);
    const isMobileWhiteTheme = useMemo(() => theme === 'white' && isMobile, [theme, isMobile]);

    const newDesignClasses = {
        [styles.open]: isOpenItem && newDesign,
        // Новый дизайн, таб закрыт и desktop версия = hover работает
        [styles.hovered]: hovered && newDesign && !isOpenItem && !isMobile,
        [styles.newItem]: newDesign,
        [styles.newDarkTheme]: theme === 'dark_y24',
    };

    return (
        <Stack>
            {zIndex => (
                <div
                    className={cn(
                        styles.item,
                        {
                            [styles.withOutDivider]: !dividerEnabled,
                            [styles.opened]: isOpenItem,
                            [(styles as typeof mobileStyles).newDesignItem]:
                                isMobileWhiteTheme && isOpenItem,
                            [(styles as typeof mobileStyles).itemBeforeOpenedItem]:
                                isMobileWhiteTheme && isPositionBeforeOpenedItem,
                        },
                        newDesignClasses,
                        className
                    )}
                    data-test-id={`${ACCORDION_ITEM}-${panelKey}`}
                    id={getAccordionItemId()}
                    style={{
                        zIndex,
                        ...(isMobileWhiteTheme && { borderRadius: isOpenItem ? borderRadius : 0 }),
                        backgroundColor,
                    }}
                >
                    {Children.map(children, child => {
                        if (isValidElement<TAccordionHeader | TAccordionPanel>(child)) {
                            return cloneElement(child as ReactElement, {
                                id,
                                panelKey,
                                prevChildBackgroundColor,
                                isLastChild,
                            });
                        }
                    })}
                </div>
            )}
        </Stack>
    );
};
